// ════════════════════════════════════════════════════════════════════════
//  Anmeldezustand für Verwaltung und Admin
//
//  Vier Zustände, absichtlich unterscheidbar: laden, abgemeldet, angemeldet mit
//  Profil, angemeldet ohne Profil. Der letzte ist kein Fehler – ein Superadmin
//  gehört zu keiner Schule, und eine eben eingeladene Lehrkraft hat noch kein
//  Profil. Wer die vier zu zwei zusammenfasst, zeigt einem dieser Fälle einen
//  Ladebalken ohne Ende.
//
//  Beide Apps teilen diesen Zustand, weil beide dieselbe Anmeldung haben. Die
//  Admin-App hatte ihn einmal selbst nachgebaut – mit einem handgetippten
//  Session-Typ und ohne `loadIdentity` – und dabei den Unterschied zwischen
//  „kein Profil“ und „kein Plattformrecht“ verloren.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { OttoClient } from "../client/client.js";
import { loadIdentity, type Identity } from "./auth.js";

export type SessionState =
  | { readonly kind: "loading" }
  | { readonly kind: "signedOut" }
  | { readonly kind: "signedIn"; readonly identity: Identity }
  /**
   * Angemeldet, aber die Rechte waren nicht zu ermitteln. Ein eigener Zustand,
   * weil das Gegenteil – „keine Rechte“ – eine ganz andere Aussage ist: die
   * eine schickt zum Warten, die andere zur Schulleitung.
   */
  | { readonly kind: "failed"; readonly reason: string };

const Context = createContext<{ state: SessionState; client: OttoClient } | null>(null);

export function SessionProvider({
  client,
  children,
}: {
  client: OttoClient;
  children: ReactNode;
}) {
  const [state, setState] = useState<SessionState>({ kind: "loading" });

  useEffect(() => {
    let alive = true;

    const apply = async (session: Parameters<typeof loadIdentity>[1] | null) => {
      if (!session) {
        if (alive) setState({ kind: "signedOut" });
        return;
      }
      try {
        const identity = await loadIdentity(client, session);
        if (alive) setState({ kind: "signedIn", identity });
      } catch (fehler) {
        if (alive) {
          setState({
            kind: "failed",
            reason: fehler instanceof Error ? fehler.message : String(fehler),
          });
        }
      }
    };

    void client.auth.getSession().then(({ data }) => apply(data.session));

    const { data } = client.auth.onAuthStateChange((_ereignis, session) => {
      // Nicht IM Callback arbeiten. supabase-js hält währenddessen die
      // Auth-Sperre; ein Aufruf, der die Sitzung braucht, geht dann ohne sie
      // hinaus – die Anfrage trägt nur den anon-Schlüssel und `auth.uid()` ist
      // in Postgres null. Für `is_platform_admin()` heißt das: false.
      //
      // Sichtbar wird das genau einmal, dafür im schlimmsten Moment: beim
      // allerersten Anmelden über den Magic Link. Danach liegt die Sitzung im
      // Speicher, `getSession()` liefert sie sofort, und der Weg über den
      // Callback fällt nicht mehr auf. Die Admin-App zeigte deshalb „Kein
      // Plattformzugang“ – einem Konto, das die Rechte hat.
      setTimeout(() => void apply(session), 0);
    });

    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo(() => ({ state, client }), [state, client]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useSession() {
  const value = useContext(Context);
  if (!value) throw new Error("useSession braucht einen SessionProvider");
  return value;
}
